import { html, css } from 'lit';
import { classMap } from 'lit/directives/class-map';
import { ifDefined } from 'lit/directives/if-defined';
import KaiElement from '../base';

const _preventFocus = (e) => e.preventDefault();

class SearchBarElement extends KaiElement {
  static get is() {
    return 'kai-searchbar';
  }

  static get properties() {
    return {
      value: { type: String, attribute: 'value', reflect: true },
      placeholder: { type: String, attribute: 'placeholder', reflect: true },
      maxlength: { type: Number, attribute: 'maxlength', reflect: true },
      withcancel: { type: Boolean },
      isheader: { type: Boolean },
      'x-inputmode': { type: String },
      // internal properties
      isFocusMode: { type: Boolean, reflect: false },
    };
  }

  static get styles() {
    return [
      super.styles,
      /* eslint-disable */
      css`
        .textfield {
          width: 100%;
          height: 6rem;
          padding: 0 2rem;
          background-color: var(--color-gs00);
          box-sizing: border-box;
          display: flex;
          flex-direction: row;
          align-items: center;
          overflow: hidden;
        }

        .textfield.isheader {
          height: 8rem;
        }

        .input-field-container {
          display: flex;
          flex-direction: row;
          flex-wrap: wrap;
          align-items: center;
          width: 100%;
          height: 3.4rem;
        }

        input {
          /*
            3.2rem is search icon size,
            1rem is gap between search icon
          */
          width: calc(100% - 3.2rem - 1rem);
          min-height: 2.4rem;
          margin-left: 1rem;
          margin-right: unset;
          color: var(--color-gs100);
          background-color: var(--color-gs00);
          border: none;
          padding: unset;
        }

        /*
          under gecko 51, use ::-moz-placeholder instead of ::placeholder
        */
        input::-moz-placeholder {
          opacity: var(--inactive-opacity);
          color: var(--color-gs100);
          text-overflow: ellipsis;
        }

        :-moz-dir(rtl) input {
          margin-left: unset;
          margin-right: 1rem;
          text-align: right;
        }

        input:required {
          box-shadow: none;
        }

        input:invalid {
          box-shadow: none;
        }

        .textfield.clearButtonEnabled input {
          /*
            3.2rem is search icon size,
            1rem is gap between search icon and clear button,
            2.4rem is clear button size,
          */
          width: calc(100% - 3.2rem - 1rem - 2.4rem - 1rem);
        }

        .search-icon {
          width: 3.2rem;
          height: 3.2rem;
          font-size: 3.2rem;
        }

        .search-icon::before {
          position: relative;
          top: -0.5rem;
          color: var(--color-gs100);
          will-change: opacity;
          transition: opacity 0.15s;
          opacity: var(--inactive-opacity);
        }

        .textfield.focus .search-icon::before {
          will-change: opacity;
          transition: opacity 0.15s;
          opacity: 1;
        }

        .textfield.hasInputValue .search-icon::before {
          opacity: 1;
        }

        .clear-button {
          width: 2.4rem;
          height: 2.4rem;
          font-size: 2.4rem;
          display: none;
        }

        .textfield.clearButtonEnabled .clear-button {
          display: block;
        }

        .clear-button::before {
          position: relative;
          top: -0.4rem;
          left: unset;
          right: -1.3rem; /* The container is out of the padding right. */
          color: var(--color-gs40);
        }

        .clear-button:-moz-dir(rtl)::before {
          left: -1.3rem;
          right: unset;
        }

        .cancel-button {
          margin-left: 1.5rem;
          margin-right: unset;
          color: var(--color-gs60);
          display: none;
        }

        .cancel-button:-moz-dir(rtl) {
          margin-left: unset;
          margin-right: 1.5rem;
        }

        .textfield.isheader .cancel-button {
          display: block;
        }

        .textfield.animationOn.cancelButtonEnabled .cancel-button {
          display: block;
          animation:
            slide-in 0.25s forwards cubic-bezier(0.3, 0, 0.25, 1),
            fade-in 0.15s forwards linear;
        }

        @keyframes slide-in {
          0% {
            transform: translateX(7rem);
          }

          100% {
            transform: translateX(0);
          }
        }

        @keyframes fade-in {
          0% {
            opacity: 0;
          }

          100% {
            opacity: 1;
          }
        }

        .input-field {
          width: 100%;
          height: 0.2rem;
          box-sizing: border-box;
          border-bottom-width: 0.2rem;
          border-bottom-style: solid;
          will-change: border-bottom-color;
          transition: border-bottom-color 0.15s;
          border-bottom-color: var(--color-gs20);
        }

        .textfield.focus .input-field {
          border-bottom-width: 0.2rem;
          border-bottom-style: solid;
          will-change: border-bottom-color;
          transition: border-bottom-color 0.3s;
          border-bottom-color: var(--theme-color);
        }
      `,
      /* eslint-enable */
    ];
  }

  constructor() {
    super();
    this.placeholder = '';
    this.value = '';
    this.withcancel = false;
    this.isheader = false;

    this.isFocusMode = false;
  }

  _fireEvent(name) {
    const event = new CustomEvent(name, {
      detail: { value: this.value },
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }

  get textfield() {
    return this.shadowRoot.getElementById('textfield');
  }

  get input() {
    return this.shadowRoot.getElementById('input');
  }

  get clearButton() {
    return this.shadowRoot.getElementById('clearButton');
  }

  get cancelButton() {
    return this.shadowRoot.getElementById('cancelButton');
  }

  /* add native input method */

  blur() {
    this.input && this.input.blur();
  }

  focus() {
    this.input && this.input.focus();
  }

  select() {
    this.input && this.input.select();
  }

  setSelectionRange(selectionStart, selectionEnd, selectionDirection) {
    this.input &&
      this.input.setSelectionRange(
        selectionStart,
        selectionEnd,
        selectionDirection
      );
  }

  render() {
    const hasInputValue = !!this.value;

    const textfieldClasses = {
      textfield: true,
      isheader: this.isheader,
      animationOn: !this.isheader,
      hasInputValue,
      focus: this.isFocusMode,
      clearButtonEnabled: this.isFocusMode && hasInputValue,
      cancelButtonEnabled: this.isFocusMode && this.withcancel,
    };

    return html`
      <div id="textfield" class="${classMap(textfieldClasses)}">
        <div class="input-field-container">
          <div
            id="search-icon"
            class="search-icon"
            data-icon="search"
            aria-hidden="true"
          >
          </div>
          <input
            id="input"
            class="subtitle-1"
            type="search"
            aria-label="."
            dir="auto"
            maxlength="${ifDefined(this.maxlength)}"
            x-inputmode="${ifDefined(this['x-inputmode'])}"
            .placeholder="${this.placeholder}"
            .value="${this.value}"
            @focus=${this._onfocus}
            @blur=${this._onblur}
            @change=${this._onchange}
            @input=${this._oninput}
          >
          </input>
          <div
            id="clearButton"
            class="clear-button"
            data-icon="input-clear"
            role="button"
            @mousedown=${_preventFocus}
            @click=${this._clickClearButton}
          >
          </div>
          <div class="input-field"></div>
        </div>
        ${
          this.withcancel
            ? html`
                <div
                  id="cancelButton"
                  class="cancel-button subtitle-1"
                  role="button"
                  @click=${this._clickCancelButton}
                >
                  Cancel
                </div>
              `
            : ''
        }
      </div>
    `;
  }

  _onfocus() {
    this.isFocusMode = true;
  }

  _onchange(e) {
    this.value = e.target.value;
    this._fireEvent('change');
  }

  _oninput(e) {
    /*
      if we don't stopPropagation() here, there are two input events fired,
      one of them is fired by kai-searchbar, another is fired by input dom.
      we use stopPropagation() to prevent input dom fire the event.
    */
    e.stopPropagation();
    this.value = e.target.value;
    this._fireEvent('input');
  }

  _onblur() {
    this.isFocusMode = false;
  }

  _clickClearButton() {
    this.value = '';
    this._fireEvent('clear');
  }

  _clickCancelButton() {
    this._fireEvent('cancel');
  }
}

customElements.define(SearchBarElement.is, SearchBarElement);
